<script setup>
import UserTimelineCarouselItem from "~/components/timeline/UserTimelineCarouselItem.vue";
import UserTimelineListItem from "~/components/timeline/UserTimelineListItem.vue";
import {computed, ref} from "vue";
import {useNavigationItems} from "~/composables/useNavigationItems";
import {useTimelineFeed} from "~/composables/useTimelineFeed";
import {userTimelineDataType} from "~/constants/userTimelineConstants.js";

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  pinned: {
    type: Boolean,
    default: false
  }
})

// Announcements carry an HTML body (contentHtml). Tapping the card opens the
// full content in a dialog (mirrors the app's bottom sheet → TlHtmlView),
// reusing CrudFormDialog as the dialog shell rather than a bespoke <v-dialog>.
const hasContent = computed(() => !!props.item.layoutData?.contentHtml?.trim());
const contentDialog = ref(false);
const contentImage = computed(() => {
  const items = props.item.layoutData?.items || [];
  return items.find((i) => i.dataType === userTimelineDataType.Image)?.url || null;
});
const onCardClick = () => {
  if (hasContent.value) contentDialog.value = true;
};
const noop = () => {};

// Every feed item is a hairline card (mockups/redesign .feed-item); pinned items
// get the brand border + a "Pinned" label and can't be scrolled away.
const classes = computed(() => ({
  'feed-item': true,
  'feed-item--pinned': props.pinned,
}))

// Feed items carry a dismissible flag and a string item key; legacy synced
// items don't, so the Dismiss affordance only renders for the new feed.
const {dismiss, dismissingLoading} = useTimelineFeed();
const dismissible = computed(() => props.item.dismissible === true && typeof props.item.id === 'string');
const onDismiss = async () => {
  try {
    await dismiss(props.item.id);
  } catch (e) {
    // surfaced by the composable; nothing to do here
  }
};

const {convertToLocalRoute} = useNavigationItems();
const buttonLinkProps = computed(() => {
  if (!props.item.layoutData?.link) {
    return {};
  }
  const localRoute = convertToLocalRoute(props.item.layoutData.link);
  return localRoute
      ? {to: localRoute}
      : {href: props.item.layoutData.link};
});
const hasLink = computed(() => !!(props.item.layoutData?.link && props.item.layoutData?.linkText));
const hasFoot = computed(() => dismissible.value || hasLink.value);
</script>

<template>
  <div :class="classes">
    <!-- Card body — clickable for announcements (opens the content dialog). -->
    <div class="fi-body" :class="{ 'fi-body--clickable': hasContent }" @click="onCardClick">
      <div v-if="pinned || item.sponsored" class="fi-labels">
        <div v-if="pinned" class="fi-pin">
          <v-icon icon="mdi-pin" size="13"/>
          Pinned
        </div>
        <!-- A club's sponsored post: an advert has to say it is one. -->
        <div v-if="item.sponsored" class="fi-sponsored">Sponsored</div>
      </div>

      <UserTimelineCarouselItem
          v-if="item.layoutData?.layoutType === 1"
          :item="item"
      />
      <UserTimelineListItem
          v-else-if="item.layoutData?.layoutType === 2 || item.layoutData?.layoutType === 0"
          :item="item"
      />
      <div v-else class="unsupported-type">
        <p>Unsupported layout type: {{ item.layoutData?.type }}</p>
      </div>
    </div>

    <!-- foot: Dismiss (left) · View link (right) — mockup .fi-foot -->
    <div v-if="hasFoot" class="fi-foot">
      <button
          v-if="dismissible"
          type="button"
          class="fi-dismiss"
          :disabled="dismissingLoading[item.id]"
          @click="onDismiss"
      >
        <v-progress-circular v-if="dismissingLoading[item.id]" indeterminate size="14" width="2"/>
        <v-icon v-else icon="mdi-close" size="16"/>
        Dismiss
      </button>
      <component
          :is="buttonLinkProps.to ? 'router-link' : 'a'"
          v-if="hasLink"
          class="fi-link"
          v-bind="buttonLinkProps"
      >
        {{ item.layoutData.linkText }}
        <v-icon icon="mdi-arrow-right" size="16"/>
      </component>
    </div>

    <!-- Announcement content dialog (reuses CrudFormDialog as the shell —
         bottom-sheet transition, fullscreen on mobile). Read-only: actions
         hidden, save is a no-op. Mirrors the app's _openContentSheet. -->
    <crud-form-dialog
        v-if="hasContent"
        :model-value="item.layoutData"
        v-model:dialog="contentDialog"
        action="update"
        :update-title="item.layoutData.title || 'Announcement'"
        :hide-actions="true"
        :max-dialog-width="600"
        :save-action="noop"
    >
      <template #default>
        <div class="fi-announce">
          <img v-if="contentImage" :src="contentImage" alt="" class="fi-announce-media"/>
          <div v-if="item.layoutData.subTitle" class="fi-announce-sub">{{ item.layoutData.subTitle }}</div>
          <div class="written-content" v-html="item.layoutData.contentHtml"/>
          <component
              :is="buttonLinkProps.to ? 'router-link' : 'a'"
              v-if="hasLink"
              class="fi-link fi-announce-link"
              v-bind="buttonLinkProps"
          >
            {{ item.layoutData.linkText }}
            <v-icon icon="mdi-arrow-right" size="16"/>
          </component>
        </div>
      </template>
    </crud-form-dialog>
  </div>
</template>

<style scoped lang="scss">
.feed-item {
  position: relative;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 10px;
}

/* pinned — brand border + label (mockup .feed-item.pinned / .fi-pin) */
.feed-item--pinned {
  border-color: rgb(var(--v-theme-primary));
}

/* clickable (announcement) cell — hover darkens the border to signal it opens.
   Applies to all clickable cells, pinned included. */
.feed-item:has(.fi-body--clickable) {
  transition: border-color 0.15s ease;
}

.feed-item:has(.fi-body--clickable:hover) {
  border-color: rgb(var(--v-theme-textSubtle));
}

.fi-body--clickable {
  cursor: pointer;
}

/* announcement content dialog body */
.fi-announce {
  .fi-announce-media {
    width: 100%;
    border-radius: 12px;
    display: block;
    margin-bottom: 14px;
  }

  .fi-announce-sub {
    font-size: 13px;
    font-weight: 600;
    color: rgb(var(--v-theme-charcoal));
    line-height: 1.6;
    margin-bottom: 12px;
  }

  .fi-announce-link {
    margin-top: 16px;
  }
}

.fi-labels {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.fi-sponsored {
  font-size: 10px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.fi-pin {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.fi-foot {
  display: flex;
  align-items: center;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.fi-dismiss {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  background: none;
  border: none;
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
  }
}

.fi-link {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.unsupported-type {
  padding: 1rem;
  border-radius: 10px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 12.5px;
  font-weight: 600;
}
</style>
